import { FC, Fragment, useCallback, useEffect, useState } from 'react';
import CharacterHero from '../components/CharacterHero';
import LoadingOverlay from '../components/LoadingOverlay';
import EXPCumulativeGraph from '../components/EXPCumulativeGraph';
import EXPDeltaGraph from '../components/EXPDeltaGraph';
import AchievementInfoPanel from '../components/AchievementInfoPanel';
import LegionInfoPanel from '../components/LegionInfoPanel';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Hidden from '@mui/material/Hidden';
import { useAppState, useEffects } from '../overmind';
import { useRouteMatch } from 'react-router-dom';
import { APICharacterData } from '../types';
import { makeStyles, createStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        outerPadding: {
            padding: "0px 16px 16px 0px",
        },
        graphContainer: {
            minHeight: 400,
            height: 400,
            width: '100%',
        },
        cardHeaderText: {
            fontFamily: "Montserrat, Verdana, Sans-Serif",
            color: "#294C60",
        },
    })
);

type CharacterPageSX = {
    gridContainer: SxProps<Theme>;
    expGraphCardContent: SxProps<Theme>;
    cardHeader: SxProps<Theme>;
    cardFullHeight: SxProps<Theme>;
}

const SXElements: CharacterPageSX = {
    gridContainer: {
        margin: 0,
        width: '100%',
    },
    expGraphCardContent: {
        height: "100%",
    },
    cardHeader: {
        background: "#EEE",
    },
    cardFullHeight: {
        height: '100%',
        border: '1px solid #CCC',
    },
} 

interface RouteMatches {
    region: string;
    name: string;
}

const CharacterPage: FC = () => {
    const classes = useStyles();
    const { API } = useEffects();
    const matches = useRouteMatch<RouteMatches>();
    const [charData, setCharData] = useState<APICharacterData>();
    const [hasLoaded, setHasLoaded] = useState<boolean>(false);
    const state = useAppState();

    const getCharacterData = useCallback((region: string, name: string) => {
        API.loadCharacterData(state, region, name).then(result => {
            let data = result.CharacterData;
            if (data !== undefined) {
                setCharData(data);
            }
        }).finally(() => {
            setHasLoaded(true);
        })
    }, [API, state]);

    useEffect(() => {
        let name = "";
        let region = "";
        if (matches !== undefined) {
            name = matches.params.name;
            region = matches.params.region;
        }

        if (name !== "" && region !== "") {
            getCharacterData(region, name);
        }
    }, [getCharacterData, matches])

    const charDetails = (): JSX.Element => {
        if (charData === undefined) {
            return <Fragment></Fragment>;
        }

        return (
            <div className={classes.outerPadding}>
                <Grid container spacing={2} sx={SXElements.gridContainer}>
                    {/* XL OR HIGHER */}
                    <Hidden xlUp>
                        <Grid item xs={12} sm={12} md={6} lg={6} xl={3}>
                            <Card sx={SXElements.cardFullHeight} square={true} elevation={0}>
                                <CardHeader
                                    title="Legion"
                                    sx={SXElements.cardHeader}
                                    classes={{
                                        title: classes.cardHeaderText,
                                    }} />
                                <CardContent sx={SXElements.expGraphCardContent}>
                                    <LegionInfoPanel legionRank={charData.LegionRank} legionLevel={charData.LegionLevel} legionPower={charData.LegionPower} legionCoins={charData.LegionCoinsPerDay} legionTimeToCap={charData.LegionCoinsPerDay} />
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={12} md={6} lg={6} xl={3}>
                            <Card sx={SXElements.cardFullHeight} square={true} elevation={0}>
                                <CardHeader
                                    title="Achievement"
                                    sx={SXElements.cardHeader}
                                    classes={{
                                        title: classes.cardHeaderText,
                                    }} />
                                <CardContent sx={SXElements.expGraphCardContent}>
                                    <AchievementInfoPanel achievementPoints={charData.AchievementPoints} achievementRank={charData.AchievementRank} />
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={12} md={6} lg={12} xl={6}>
                            <Card sx={SXElements.cardFullHeight} square={true} elevation={0}>
                                <CardHeader
                                    title="Daily EXP"
                                    sx={SXElements.cardHeader}
                                    classes={{
                                        title: classes.cardHeaderText,
                                    }} />
                                <CardContent sx={SXElements.expGraphCardContent}>
                                    <div className={classes.graphContainer}>
                                        <EXPDeltaGraph data={charData.GraphData} />
                                    </div>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={12} md={6} lg={12} xl={6}>
                            <Card sx={SXElements.cardFullHeight} square={true} elevation={0}>
                                <CardHeader
                                    title="Total EXP"
                                    sx={SXElements.cardHeader}
                                    classes={{
                                        title: classes.cardHeaderText,
                                    }} />
                                <CardContent sx={SXElements.expGraphCardContent}>
                                    <div className={classes.graphContainer}>
                                        <EXPCumulativeGraph data={charData.GraphData} level={charData.Level} />
                                    </div>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Hidden>
                    <Hidden lgDown>
                        <Grid item lg={12} xl={6}>
                            <Card sx={SXElements.cardFullHeight} square={true} elevation={0}>
                                <CardHeader
                                    title="Daily EXP"
                                    sx={SXElements.cardHeader}
                                    classes={{
                                        title: classes.cardHeaderText,
                                    }} />
                                <CardContent sx={SXElements.expGraphCardContent}>
                                    <div className={classes.graphContainer}>
                                        <EXPDeltaGraph data={charData.GraphData} />
                                    </div>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item lg={6} xl={3}>
                            <Card sx={SXElements.cardFullHeight} square={true} elevation={0}>
                                <CardHeader
                                    title="Legion"
                                    sx={SXElements.cardHeader}
                                    classes={{
                                        title: classes.cardHeaderText,
                                    }} />
                                <CardContent sx={SXElements.expGraphCardContent}>
                                    <LegionInfoPanel legionRank={charData.LegionRank} legionLevel={charData.LegionLevel} legionPower={charData.LegionPower} legionCoins={charData.LegionCoinsPerDay} legionTimeToCap={charData.LegionCoinsPerDay} />
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item lg={6} xl={3}>
                            <Card sx={SXElements.cardFullHeight} square={true} elevation={0}>
                                <CardHeader
                                    title="Achievement"
                                    sx={SXElements.cardHeader}
                                    classes={{
                                        title: classes.cardHeaderText,
                                    }} />
                                <CardContent sx={SXElements.expGraphCardContent}>
                                    <AchievementInfoPanel achievementPoints={charData.AchievementPoints} achievementRank={charData.AchievementRank} />
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={12} md={6} lg={12} xl={6}>
                            <Card sx={SXElements.cardFullHeight} square={true} elevation={0}>
                                <CardHeader
                                    title="Total EXP"
                                    sx={SXElements.cardHeader}
                                    classes={{
                                        title: classes.cardHeaderText,
                                    }} />
                                <CardContent sx={SXElements.expGraphCardContent}>
                                    <div className={classes.graphContainer}>
                                        <EXPCumulativeGraph data={charData.GraphData} level={charData.Level} />
                                    </div>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Hidden>
                </Grid>
            </div>
        );
    }

    return (
        <Fragment>
            <CharacterHero hasLoaded={hasLoaded} charData={charData} />
            {charDetails()}
            <LoadingOverlay isLoading={!hasLoaded} />
        </Fragment>
    );
}

export default CharacterPage;

